import {
  type ComponentType,
  type SVGProps,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import { LuChevronDown } from "react-icons/lu";
import { siteTexts } from "../i18n/siteTexts";
import "./PageSizeSelector.css";

export type PageSizeValue = 5 | 10 | 15 | "all";

const DEFAULT_OPTIONS: PageSizeValue[] = [5, 10, 15, "all"];
type IconLike = ComponentType<SVGProps<SVGSVGElement>>;
const ChevronDownIcon = LuChevronDown as unknown as IconLike;

type PageSizeSelectorProps = {
  ariaLabel?: string;
  className?: string;
  onChange: (value: PageSizeValue) => void;
  options?: PageSizeValue[];
  value: PageSizeValue;
};

function isPageSizeValue(value: unknown): value is PageSizeValue {
  return value === 5 || value === 10 || value === 15 || value === "all";
}

function pageSizeLabel(value: PageSizeValue) {
  const labels = siteTexts.common.pageSize;
  if (value === "all") return labels.all;
  return labels.perPage.replace("{count}", String(value));
}

export function useStoredPageSize(
  storageKey: string,
  defaultValue: PageSizeValue = 10,
) {
  const [value, setValue] = useState<PageSizeValue>(() => {
    if (typeof window === "undefined") return defaultValue;
    const stored = window.localStorage.getItem(storageKey);
    const parsed = stored === "all" ? "all" : Number(stored);
    return isPageSizeValue(parsed) ? parsed : defaultValue;
  });

  const updateValue = (nextValue: PageSizeValue) => {
    setValue(nextValue);
    if (typeof window !== "undefined") {
      window.localStorage.setItem(storageKey, String(nextValue));
    }
  };

  return [value, updateValue] as const;
}

export function resolvePageSizeLimit(value: PageSizeValue, totalItems: number) {
  if (value === "all") return Math.max(1, totalItems);
  return value;
}

export default function PageSizeSelector({
  ariaLabel,
  className = "",
  onChange,
  options = DEFAULT_OPTIONS,
  value,
}: PageSizeSelectorProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const listboxId = useId();
  const labels = siteTexts.common.pageSize;

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  return (
    <div
      className={`page-size-selector${className ? ` ${className}` : ""}`}
      ref={rootRef}
    >
      <button
        type="button"
        className="page-size-selector__button"
        aria-label={ariaLabel || labels.ariaLabel}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-controls={listboxId}
        onClick={() => setOpen((current) => !current)}
      >
        <span>{pageSizeLabel(value)}</span>
        <ChevronDownIcon className="page-size-selector__chevron" />
      </button>
      {open && (
        <div
          className="page-size-selector__menu"
          id={listboxId}
          role="listbox"
          aria-label={ariaLabel || labels.ariaLabel}
        >
          {options.map((option) => (
            <button
              key={String(option)}
              type="button"
              className={`page-size-selector__option${
                value === option ? " is-selected" : ""
              }`}
              role="option"
              aria-selected={value === option}
              onClick={() => {
                onChange(option);
                setOpen(false);
              }}
            >
              {pageSizeLabel(option)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
